import { Headline } from '@vkontakte/vkui';
import trialPromoImg from 'img/aside-promo-modal/trial-promo.jpg';
import Lottie from 'lottie-react';
import React, { memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { openVkBuyModalWithSidebar } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.helpers';
import { EPaymentModalType } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.types';
import { getFeaturesAttachesTrialPromo, getFeaturesAttachesTrialPromoRound } from 'reactApp/modules/features/features.selectors';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { RootState } from 'reactApp/store';
import { AsidePromoModal } from 'reactApp/ui/AsidePromoModal/AsidePromoModal';
import { EAsidePromoModalTheme } from 'reactApp/ui/AsidePromoModal/AsidePromoModal.types';
import { ESplashScreenAction, useSplashScreenAnalytics } from 'reactApp/ui/Mobile/SplashScreen/SplashScreen.analytics';
import { getPromoContent } from 'reactApp/ui/TrialButtonPromo/TrialButtonPromo.helpers';

import giftAnimation from './giftAnimation.json';
import styles from './TrialButtonPromo.css';

interface Props {
    productId: string;
}

export const TrialButtonPromo = memo(({ productId }: Props): ReactElement | null => {
    const trialPromoFeature = useSelector(getFeaturesAttachesTrialPromo);
    const trialPromoRoundFeature = useSelector(getFeaturesAttachesTrialPromoRound);

    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, productId));

    const [showBanner, setShowBanner] = useState<boolean>(false);
    const [showFakeBanner, setShowFakeBanner] = useState<boolean>(false);
    const [showButton, setShowButton] = useState<boolean>(true);

    const { title, buttonText, text } = getPromoContent(product);

    const sendSplashScreenAnalytics = useSplashScreenAnalytics({ name: 'attaches-trial', button_name: buttonText });

    useEffect(() => {
        if (showFakeBanner) {
            sendSplashScreenAnalytics(ESplashScreenAction.show);
        }
    }, [sendSplashScreenAnalytics, showFakeBanner]);

    const onShowPromoBanner = useCallback(() => {
        setShowFakeBanner(true);
    }, []);

    const onPromoButtonClick = useCallback(() => {
        openVkBuyModalWithSidebar({ productId, type: EPaymentModalType.attachesTrial });
        sendSplashScreenAnalytics(ESplashScreenAction.click);
        setShowButton(true);
        setShowBanner(false);
    }, [productId, sendSplashScreenAnalytics]);

    const onBannerClose = useCallback(() => {
        setShowButton(true);
        setShowBanner(false);
        sendSplashScreenAnalytics(ESplashScreenAction.close);
    }, []);

    const button = useMemo(
        () =>
            trialPromoFeature ? (
                <div className={styles.button} onMouseOver={onShowPromoBanner}>
                    <Lottie animationData={giftAnimation} />
                </div>
            ) : (
                <div className={styles.buttonWithSpace} onMouseOver={onShowPromoBanner}>
                    <div className={styles.gift}>
                        <Lottie animationData={giftAnimation} />
                    </div>
                    <Headline level="1">+{product?.space?.value}</Headline>
                </div>
            ),
        [onShowPromoBanner, product?.space?.value, trialPromoFeature]
    );

    useEffect(() => {
        if (product?.id) {
            sendSplashScreenAnalytics(ESplashScreenAction.preview);
        }
    }, [product?.id, sendSplashScreenAnalytics]);

    if (!product || (!trialPromoRoundFeature && !trialPromoFeature)) {
        return null;
    }

    return (
        <div>
            {showButton && button}

            <CSSTransition
                in={showFakeBanner}
                timeout={200}
                classNames={{
                    enter: styles.animation_fake_appear,
                    enterActive: styles.animation_fake_appear_active,
                    enterDone: styles.animation_fake_appear_done,
                }}
                onEnter={() => setShowButton(false)}
                onEntered={() => setShowBanner(true)}
            >
                <div className={styles.fakeBanner} />
            </CSSTransition>

            <CSSTransition
                in={showBanner}
                timeout={300}
                classNames={{
                    enter: styles.animation_banner_appear,
                    enterActive: styles.animation_banner_appear_active,
                    enterDone: styles.animation_banner_appear_done,
                }}
                onEntered={() => setShowFakeBanner(false)}
            >
                <div className={styles.banner}>
                    {showBanner && (
                        <AsidePromoModal
                            fixed={false}
                            title={title}
                            text={text}
                            buttonText={buttonText}
                            imageUrl={trialPromoImg}
                            onClose={onBannerClose}
                            onClick={onPromoButtonClick}
                            theme={EAsidePromoModalTheme.dark}
                        />
                    )}
                </div>
            </CSSTransition>
        </div>
    );
});

TrialButtonPromo.displayName = 'TrialButtonPromo';
